import React, { useContext } from 'react'
import {
  bool,
  shape,
  arrayOf,
  string,
  oneOf,
  exact,
  oneOfType,
  number,
  object,
} from 'prop-types'
import * as Styled from './Quality.styles'
import { isRequiredIf } from 'settings/propTypeDefinitions'

// component stack
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import { Accordion } from '../'
import { IndicatorTable } from './partials/'
import { Tooltip } from 'components/atoms'
import { QSR_ORDER } from './helpers/constants'

/**
 * table mapper component
 * @param isTreatmentReason
 * @param type
 * @param tableData
 * @returns {boolean|JSX.Element}
 * @constructor
 */
const Quality = ({ isTreatmentReason, type, tableData }) => {
  const textContext = useContext(TextContext)
  const { tooltip, data, description } = tableData
  const countTables = Array.isArray(data) ? data.length : false

  const sortQualityData = (unsortedData) => {
    const sortFn = (a, b) => {
      const aIdx = QSR_ORDER.findIndex((el) => el === a.id)
      const bIdx = QSR_ORDER.findIndex((el) => el === b.id)
      if (aIdx < 0) return 1
      if (bIdx < 0) return -1

      return aIdx - bIdx
    }
    const sortedData = [...unsortedData]
    return sortedData.sort(sortFn)
  }

  const getIndicatorTable = ({
    type,
    data,
    treatmentCases,
    description = false,
    tableIndex = undefined,
  }) => {
    if ('undefined' === typeof data.table) return false

    return (
      <IndicatorTable
        type={ type }
        tableIndex={ tableIndex }
        data={ data }
        treatmentCases={ treatmentCases }
        isTreatmentReason={ isTreatmentReason }
        tooltip={ isTreatmentReason ? tooltip : false }
        description={ description }
      />
    )
  }

  return (
    countTables > 0 && (
      <Styled.HospitalQuality>
        {!isTreatmentReason && 'undefined' !== typeof data && (
          <>
            <div className='tooltip-container'>
              <span className='headline'>
                {textContext[`quality.headline.${ type }`]}
              </span>

              {tooltip && (
                <Tooltip
                  arrow='left'
                  className='tooltip'
                  dialogTitle={
                    tooltip.title !== ''
                      ? tooltip.title
                      : textContext[`quality.headline.${ type }`]
                  }
                  infoIconId={ `info-icon-${ type }` }
                  tooltipId={ `tooltip-${ type }` }>
                  {tooltip.tip}
                </Tooltip>
              )}
            </div>

            {sortQualityData(data).map((table, index) => (
              <Accordion key={ index } label={ table.headline } type={ 'UNDERLINED' }>
                {getIndicatorTable({
                  type,
                  data: table,
                  treatmentCases: 'qsr' === type ? table.treatmentCases : false,
                })}
              </Accordion>
            ))}

            {description && <p>{description}</p>}
          </>
        )}
        {isTreatmentReason &&
          data &&
          data.map((table, index) => (
            <div key={ index }>
              {getIndicatorTable({
                type,
                data: table,
                treatmentCases: 'qsr' === type ? table.treatmentCases : false,
                description:
                  index + 1 === countTables && description
                    ? description
                    : false,
                tableIndex: index,
              })}
            </div>
          ))}
      </Styled.HospitalQuality>
    )
  )
}

Quality.defaultProps = {
  isTreatmentReason: false,
}

Quality.propTypes = {
  isTreatmentReason: bool,
  type: oneOf(['qsr', 'iqtig']).isRequired,
  tableData: shape({
    tooltip: shape({
      title: string,
      tip: string,
    }),
    description: string,
    data: arrayOf(
      shape({
        headline: string.isRequired,
        table: arrayOf(
          exact({
            id: string,
            indicator: string.isRequired,
            order: number,
            tooltip: shape({
              title: string.isRequired,
              tip: string.isRequired,
            }).isRequired,
            result: exact({
              rating: string.isRequired,
              value: oneOfType([string, number]),
              range: string.isRequired,
              referenceRange: isRequiredIf(
                string,
                (props) => 'iqtig' === props.type
              ),
              federalAverage: oneOfType([number, string]),
            }).isRequired,
            explanation: object.isRequired,
          })
        ),
      })
    ),
  }).isRequired,
}

export default Quality
